import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';

import { Pager } from '@/components/pager';
import { Badge, Callout, Field, buttonClass, inputClass, textareaClass } from '@/components/ui';
import { AddressFields } from '@/components/admin/address-fields';
import { blankAddress, isBlankAddress, readAddress, toPostalAddress, type PostalAddress } from '@/lib/address';
import { formatCents } from '@/lib/money';
import type {
  Supplier, SupplierContact, SupplierItemHit, SupplierItemRow, SupplierPage,
} from '@/lib/stock-types';
import { supplierItemsQuery, supplierQuery } from '@/queries';

/**
 * One supplier, whole. Spec 013, Guy's minutes of 29 October, Part 3.
 *
 * Its company details with a separate email for purchase orders (FR-1301 to
 * FR-1305), the people there (FR-1310 to FR-1313), and the items it sells
 * (FR-1320 to FR-1325). Every change goes through the same routes the stock
 * item panel calls, so a link made there and a link made here are one link.
 */

async function call(path: string, init: RequestInit) {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  const json = (await res.json().catch(() => null)) as
    | { error?: { message?: string }; detail?: string; outcome?: string } | null;
  if (!res.ok) throw new Error(json?.error?.message ?? `That did not save (${res.status})`);
  return json;
}

const box = 'rounded-[10px] border border-line bg-panel';
const heading = 'mb-3 text-[18px] font-bold';

/** The supplier page and the list read again, after any change. */
function useRefresh(supplierId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['admin', 'suppliers'] })
    .then(() => qc.invalidateQueries({ queryKey: ['admin', 'suppliers', supplierId] }));
}

export default function AdminSupplierPage() {
  const { supplierId } = useParams({ from: '/admin/admin/suppliers/$supplierId' });
  const page = useSuspenseQuery(supplierQuery(supplierId)).data;
  if (!page) {
    return (
      <div className={`${box} p-8 text-center`}>
        <p className="text-[16px] font-semibold">There is no supplier with that id.</p>
        <Link to="/admin/suppliers" className="mt-2 inline-block text-[14px] underline">
          All suppliers
        </Link>
      </div>
    );
  }
  return <SupplierBody page={page} />;
}

function SupplierBody({ page }: { page: SupplierPage }) {
  const s = page.supplier;
  return (
    <div>
      <Link to="/admin/suppliers" className="text-[14px] text-muted">← All suppliers</Link>
      <Header page={page} />
      <Details supplier={s} mayEdit={page.may_edit} />
      <Contacts supplierId={s.id} contacts={page.contacts} mayEdit={page.may_edit} />
      <Items supplierId={s.id} mayEdit={page.may_edit} />
    </div>
  );
}

/**
 * The name, and Switch off or Switch on. FR-1304.
 *
 * The control existed in the route and not on the screen: a supplier could be
 * switched off by Remove, and nothing switched it back on.
 */
function Header({ page }: { page: SupplierPage }) {
  const s = page.supplier;
  const refresh = useRefresh(s.id);
  const [error, setError] = useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: async () => call(`/api/suppliers/${s.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active: !s.active }),
    }),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="mb-6 mt-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">
            {s.name} {!s.active && <Badge tone="warn">switched off</Badge>}
          </h1>
          {s.code && <p className="mt-1 font-mono text-[14px] text-muted">{s.code}</p>}
        </div>
        {page.may_edit && (
          <button
            type="button"
            className={buttonClass('secondary', 'sm')}
            disabled={toggle.isPending}
            onClick={() => toggle.mutate()}
          >
            {s.active ? 'Switch off' : 'Switch on'}
          </button>
        )}
      </div>
      {!s.active && (
        <p className="mt-2 max-w-[62ch] text-[13px] text-muted">
          This supplier is switched off. It is not offered on a new purchase order.
          {page.open_orders > 0
            ? ` Its ${page.open_orders} open ${page.open_orders === 1 ? 'order stays' : 'orders stay'}, and you can still receive against ${page.open_orders === 1 ? 'it' : 'them'}.`
            : ' It has no open orders.'}
        </p>
      )}
      {error && <Callout tone="danger" title={error} className="mt-3" />}
    </div>
  );
}

function AddressText({ value, empty }: { value: PostalAddress | null; empty: string }) {
  const a = readAddress(value);
  if (!a.hasAnything) return <span className="text-muted">{empty}</span>;
  return (
    <span>
      {[...a.street, a.locality, a.country].filter(Boolean).join(', ')}
    </span>
  );
}

/**
 * The company details. FR-1301 to FR-1305.
 *
 * Two emails, and the screen says which one a purchase order goes to: the
 * purchase order email, or the general email when there is none. A contact's
 * email is never where an order goes by itself (FR-1312).
 */
function Details({ supplier: s, mayEdit }: { supplier: Supplier; mayEdit: boolean }) {
  const [editing, setEditing] = useState(false);
  const rows: Array<[string, React.ReactNode]> = [
    ['Supplier code', s.code ?? <span className="text-muted">None</span>],
    ['Purchase order email', s.po_email ?? (
      <span className="text-muted">
        {s.general_email ? 'None. Orders go to the general email.' : 'None. With no email, an order is sent without one.'}
      </span>
    )],
    ['General email', s.general_email ?? <span className="text-muted">None</span>],
    ['Main phone', s.phone ?? <span className="text-muted">None</span>],
    ['Address', <AddressText key="a" value={s.address} empty="None" />],
    ['Pickup address', s.pickup_address
      ? <AddressText key="p" value={s.pickup_address} empty="None" />
      : <span className="text-muted">Same as the address</span>],
    ['Lead time', `${s.lead_days} ${s.lead_days === 1 ? 'day' : 'days'}`],
    ['Notes', s.notes ?? <span className="text-muted">None</span>],
  ];

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-[18px] font-bold">Company details</h2>
        {mayEdit && !editing && (
          <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <EditDetails supplier={s} onDone={() => setEditing(false)} />
      ) : (
        <dl className={`${box} grid max-w-[760px] gap-x-6 gap-y-2 p-4 text-[14px] sm:grid-cols-[180px_1fr]`}>
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-[13px] text-muted">{label}</dt>
              <dd className="break-words">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/**
 * CHANGING A SUPPLIER, AND TAKING ONE OFF THE LIST. T520.
 *
 * Guy reported this shape twice, about locations and about stock items: "I can
 * not remove stock Locations or edit only Add". Suppliers had the same hole.
 *
 * Removing behaves in two ways and says which. A supplier never bought from is
 * a typo and goes. One that has purchase orders against it is switched off
 * instead, because every order and receipt names it.
 */
function EditDetails({ supplier: s, onDone }: { supplier: Supplier; onDone: () => void }) {
  const refresh = useRefresh(s.id);
  const navigate = useNavigate();
  const [name, setName] = useState(s.name);
  const [code, setCode] = useState(s.code ?? '');
  const [poEmail, setPoEmail] = useState(s.po_email ?? '');
  const [generalEmail, setGeneralEmail] = useState(s.general_email ?? '');
  const [phone, setPhone] = useState(s.phone ?? '');
  const [address, setAddress] = useState<PostalAddress>(s.address ? toPostalAddress(s.address) : blankAddress());
  const [samePickup, setSamePickup] = useState(s.pickup_address == null);
  const [pickup, setPickup] = useState<PostalAddress>(s.pickup_address ? toPostalAddress(s.pickup_address) : blankAddress());
  const [leadDays, setLeadDays] = useState(String(s.lead_days));
  const [notes, setNotes] = useState(s.notes ?? '');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => call(`/api/suppliers/${s.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name, code, po_email: poEmail, general_email: generalEmail, phone, notes,
        // A blank form is no address. FR-1303 uses the customer's shape and checks.
        address: isBlankAddress(address) ? null : address,
        // "Same as address" is no pickup address of its own. FR-1301.
        pickup_address: samePickup ? null : pickup,
        lead_days: leadDays.trim() === '' ? null : Number(leadDays),
      }),
    }),
    onSuccess: () => { setError(null); onDone(); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: async () => call(`/api/suppliers/${s.id}`, { method: 'DELETE' }),
    onSuccess: (r) => {
      setError(null);
      setConfirming(false);
      if (r?.outcome === 'deleted') {
        void navigate({ to: '/admin/suppliers' }).then(refresh);
        return;
      }
      // "Removed" and "switched off" are different outcomes, and somebody will
      // look for this supplier again.
      setSaid(r?.detail ?? null);
      void refresh();
    },
    onError: (e: Error) => { setConfirming(false); setError(e.message); },
  });

  return (
    <form
      className={`${box} max-w-[760px] p-4`}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor="sup-name">
          <input id="sup-name" className={inputClass} value={name} required onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Supplier code" htmlFor="sup-code" optional hint="Your short name for them, for example GILD">
          <input id="sup-code" className={inputClass} value={code} maxLength={20} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label="Purchase order email" htmlFor="sup-po-email" optional hint="Purchase orders go here first">
          <input id="sup-po-email" className={inputClass} type="email" value={poEmail} onChange={(e) => setPoEmail(e.target.value)} />
        </Field>
        <Field label="General email" htmlFor="sup-general-email" optional hint="Orders go here when there is no purchase order email">
          <input id="sup-general-email" className={inputClass} type="email" value={generalEmail} onChange={(e) => setGeneralEmail(e.target.value)} />
        </Field>
        <Field label="Main phone" htmlFor="sup-phone" optional>
          <input id="sup-phone" className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Lead time in days" htmlFor="sup-lead" hint="What turns a low stock warning into a date to order by">
          <input id="sup-lead" className={inputClass} value={leadDays} inputMode="numeric" onChange={(e) => setLeadDays(e.target.value)} />
        </Field>
      </div>

      <h3 className="mb-2 mt-5 text-[14px] font-semibold">Address</h3>
      <AddressFields idPrefix="sup-address" value={address} onChange={setAddress} />

      <h3 className="mb-2 mt-5 text-[14px] font-semibold">Pickup address</h3>
      <label className="mb-3 flex items-center gap-2 text-[14px]">
        <input type="checkbox" checked={samePickup} onChange={(e) => setSamePickup(e.target.checked)} />
        Same as the address
      </label>
      {!samePickup && <AddressFields idPrefix="sup-pickup" value={pickup} onChange={setPickup} />}

      <div className="mt-5">
        <Field label="Notes" htmlFor="sup-notes" optional>
          <textarea id="sup-notes" className={textareaClass} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>

      {error && <Callout tone="danger" title={error} className="mt-3" />}
      {said && <p className="mt-3 text-[13px] text-muted">{said}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending}>
          {save.isPending ? 'Saving' : 'Save'}
        </button>
        <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => { setError(null); onDone(); }}>
          Cancel
        </button>
        <span className="flex-1" />
        {/* A tertiary link on the far side, and it asks first. */}
        {confirming ? (
          <span className="flex items-center gap-2">
            <span className="text-[12.5px]">Remove this supplier?</span>
            <button
              type="button" className={buttonClass('secondary', 'sm')} disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? 'Removing' : 'Yes, remove'}
            </button>
            <button type="button" className="text-[12.5px] text-muted underline" onClick={() => setConfirming(false)}>
              No
            </button>
          </span>
        ) : (
          <button
            type="button" className="text-[12.5px] text-[color:var(--color-danger-ink)] underline"
            onClick={() => setConfirming(true)}
          >
            Remove this supplier
          </button>
        )}
      </div>
      <p className="mt-2 text-[12px] text-muted">
        A supplier you have never bought from is removed. One you have is
        switched off instead, so your purchase history still makes sense.
      </p>
    </form>
  );
}

/**
 * The people at the supplier. FR-1310 to FR-1313.
 *
 * Remove switches a contact off and keeps it, under "Removed contacts", with
 * Put back. A contact named on an order that went out is part of the record.
 */
function Contacts(
  { supplierId, contacts, mayEdit }:
  { supplierId: string; contacts: SupplierContact[]; mayEdit: boolean },
) {
  const [adding, setAdding] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const active = contacts.filter((c) => c.active);
  const removed = contacts.filter((c) => !c.active);

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-[18px] font-bold">Contacts</h2>
        {mayEdit && !adding && (
          <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setAdding(true)}>
            Add a contact
          </button>
        )}
      </div>
      {adding && <ContactForm supplierId={supplierId} onDone={() => setAdding(false)} />}
      {active.length === 0 && !adding ? (
        <p className={`${box} p-4 text-[14px] text-muted`}>
          No contacts yet. Add the people you deal with, for example the sales
          representative and the accounts team.
        </p>
      ) : (
        <div className={box}>
          {active.map((c) => (
            <ContactRow key={c.id} supplierId={supplierId} contact={c} mayEdit={mayEdit} />
          ))}
        </div>
      )}
      <p className="mt-2 text-[12.5px] text-muted">
        A purchase order goes to the purchase order email, or to the general
        email. A contact gets a copy only when you choose that contact for one
        send.
      </p>
      {removed.length > 0 && (
        <div className="mt-3">
          <button type="button" className="text-[13px] underline" onClick={() => setShowRemoved(!showRemoved)}>
            {showRemoved ? 'Hide' : 'Show'} removed contacts ({removed.length})
          </button>
          {showRemoved && (
            <div className={`${box} mt-2`}>
              {removed.map((c) => (
                <ContactRow key={c.id} supplierId={supplierId} contact={c} mayEdit={mayEdit} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ContactRow(
  { supplierId, contact: c, mayEdit }:
  { supplierId: string; contact: SupplierContact; mayEdit: boolean },
) {
  const refresh = useRefresh(supplierId);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: async () => call(`/api/suppliers/${supplierId}/contacts/${c.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active: !c.active }),
    }),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  if (editing) {
    return (
      <div className="border-b border-line p-3 last:border-0">
        <ContactForm supplierId={supplierId} contact={c} onDone={() => setEditing(false)} />
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3 last:border-0">
      <span className="flex flex-col text-[14px]">
        <span className="font-semibold">
          {c.name}
          {c.position && <span className="font-normal text-muted"> · {c.position}</span>}
        </span>
        <span className="text-[13px] text-muted">
          {[c.email, c.phone].filter(Boolean).join(' · ') || 'No email or phone'}
        </span>
        {c.note && <span className="text-[13px] text-muted">{c.note}</span>}
        {error && <span role="alert" className="text-[13px] text-danger">{error}</span>}
      </span>
      {mayEdit && (
        <span className="flex items-center gap-2">
          {c.active && (
            <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          <button
            type="button" className="text-[12.5px] underline" disabled={toggle.isPending}
            onClick={() => toggle.mutate()}
          >
            {c.active ? 'Remove' : 'Put back'}
          </button>
        </span>
      )}
    </div>
  );
}

function ContactForm(
  { supplierId, contact, onDone }:
  { supplierId: string; contact?: SupplierContact; onDone: () => void },
) {
  const refresh = useRefresh(supplierId);
  const key = contact?.id ?? 'new';
  const [name, setName] = useState(contact?.name ?? '');
  const [position, setPosition] = useState(contact?.position ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [note, setNote] = useState(contact?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const body = () => JSON.stringify({ name, position, email, phone, note });

  const save = useMutation({
    mutationFn: async () => (contact
      ? call(`/api/suppliers/${supplierId}/contacts/${contact.id}`, { method: 'PATCH', body: body() })
      : call(`/api/suppliers/${supplierId}/contacts`, { method: 'POST', body: body() })),
    onSuccess: () => { setError(null); onDone(); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className={`${box} mb-3 p-3`}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor={`con-name-${key}`}>
          <input id={`con-name-${key}`} className={inputClass} value={name} required onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Position" htmlFor={`con-position-${key}`} optional>
          <input id={`con-position-${key}`} className={inputClass} value={position} onChange={(e) => setPosition(e.target.value)} />
        </Field>
        <Field label="Email" htmlFor={`con-email-${key}`} optional>
          <input id={`con-email-${key}`} className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Phone" htmlFor={`con-phone-${key}`} optional>
          <input id={`con-phone-${key}`} className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
      </div>
      <div className="mt-3">
        <Field label="Note" htmlFor={`con-note-${key}`} optional>
          <input id={`con-note-${key}`} className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      {error && <Callout tone="danger" title={error} className="mt-3" />}
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Saving' : 'Save'}
        </button>
        <button type="button" className={buttonClass('secondary', 'sm')} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * ITEMS FROM THIS SUPPLIER. FR-1320 to FR-1323.
 *
 * One page at a time, because the list grows with every receipt (FR-1325).
 * The last price paid shows only to somebody who can see costs; for anybody
 * else the column is not there, rather than $0.00.
 */
function Items({ supplierId, mayEdit }: { supplierId: string; mayEdit: boolean }) {
  const [page, setPage] = useState(1);
  const items = useQuery(supplierItemsQuery(supplierId, page));
  const data = items.data;

  return (
    <section className="mb-8">
      <h2 className={heading}>Items from this supplier</h2>
      {mayEdit && <AddItems supplierId={supplierId} />}
      {items.isError && <Callout tone="danger" title={(items.error as Error).message} />}
      {items.isPending && <p className="text-[14px] text-muted">Reading the items.</p>}
      {data && data.total === 0 && (
        <p className={`${box} p-4 text-[14px] text-muted`}>
          No items yet. Add the items you buy from this supplier. An item is also
          added when you receive it on a purchase order from this supplier.
        </p>
      )}
      {data && data.total > 0 && (
        <div className={`${box} overflow-x-auto`}>
          <table className="w-full min-w-[760px] text-left text-[13.5px]">
            <thead className="border-b border-line text-[12px] uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">Colour</th>
                <th className="px-3 py-2">Size</th>
                <th className="px-3 py-2">Supplier&apos;s code</th>
                {data.costs_visible && <th className="px-3 py-2">Last price paid</th>}
                <th className="px-3 py-2 text-right">On hand</th>
                {mayEdit && <th className="px-3 py-2"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <ItemRow
                  key={r.item_id} supplierId={supplierId} row={r}
                  costsVisible={data.costs_visible} mayEdit={mayEdit}
                />
              ))}
            </tbody>
          </table>
          <Pager
            page={data.page} pages={data.pages} total={data.total} limit={data.limit}
            noun="item" onPage={setPage}
          />
        </div>
      )}
    </section>
  );
}

function ItemRow(
  { supplierId, row: r, costsVisible, mayEdit }:
  { supplierId: string; row: SupplierItemRow; costsVisible: boolean; mayEdit: boolean },
) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(r.supplier_code ?? '');
  const [pack, setPack] = useState(String(r.pack_qty));
  const [moq, setMoq] = useState(String(r.moq));
  const [note, setNote] = useState(r.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'suppliers', supplierId] });

  const save = useMutation({
    mutationFn: async () => call(`/api/suppliers/${supplierId}/items/${r.item_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ supplier_code: code, pack_qty: Number(pack), moq: Number(moq), note }),
    }),
    onSuccess: () => { setError(null); setEditing(false); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  const unlink = useMutation({
    mutationFn: async () => call(`/api/suppliers/${supplierId}/items/${r.item_id}`, { method: 'DELETE' }),
    onSuccess: () => { setError(null); void refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <>
      <tr className="border-b border-line last:border-0">
        <td className="px-3 py-2 font-mono">
          <Link to="/admin/stock" search={{ open: r.item_id }} className="underline">{r.code ?? 'Hidden'}</Link>
          {r.main && <> <Badge tone="ok">main supplier</Badge></>}
        </td>
        <td className="px-3 py-2">{r.product ?? ''}</td>
        <td className="px-3 py-2">{r.colour ?? ''}</td>
        <td className="px-3 py-2">{r.size ?? ''}</td>
        <td className="px-3 py-2 font-mono">{r.supplier_code ?? <span className="font-sans text-muted">None</span>}</td>
        {costsVisible && (
          <td className="px-3 py-2">
            {r.last_price_cents == null
              ? <span className="text-muted">Not bought yet</span>
              : <>{formatCents(r.last_price_cents)} <span className="text-muted">on {new Date(r.last_price_at!).toLocaleDateString('en-AU')}</span></>}
          </td>
        )}
        <td className="px-3 py-2 text-right">{r.on_hand ?? ''}</td>
        {mayEdit && (
          <td className="whitespace-nowrap px-3 py-2 text-right">
            <button type="button" className="text-[12.5px] underline" onClick={() => setEditing(!editing)}>
              {editing ? 'Close' : 'Edit'}
            </button>
            {/* The main supplier's link stays while it is main (FR-1321). */}
            {!r.main && (
              <button
                type="button" className="ml-2 text-[12.5px] underline" disabled={unlink.isPending}
                onClick={() => unlink.mutate()}
              >
                Remove
              </button>
            )}
          </td>
        )}
      </tr>
      {(editing || error) && (
        <tr className="border-b border-line bg-raised">
          <td colSpan={8} className="px-3 py-3">
            {editing && (
              <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
                <Field label="Supplier's code" htmlFor={`sc-${r.item_id}`} optional>
                  <input id={`sc-${r.item_id}`} className={`${inputClass} w-[200px]`} value={code} maxLength={60} onChange={(e) => setCode(e.target.value)} />
                </Field>
                <Field label="Pack size" htmlFor={`pk-${r.item_id}`}>
                  <input id={`pk-${r.item_id}`} className={`${inputClass} w-[90px]`} value={pack} inputMode="decimal" onChange={(e) => setPack(e.target.value)} />
                </Field>
                <Field label="Minimum order" htmlFor={`mo-${r.item_id}`}>
                  <input id={`mo-${r.item_id}`} className={`${inputClass} w-[90px]`} value={moq} inputMode="decimal" onChange={(e) => setMoq(e.target.value)} />
                </Field>
                <Field label="Note" htmlFor={`nt-${r.item_id}`} optional>
                  <input id={`nt-${r.item_id}`} className={`${inputClass} w-[220px]`} value={note} onChange={(e) => setNote(e.target.value)} />
                </Field>
                <button type="submit" className={buttonClass('primary', 'sm')} disabled={save.isPending}>
                  {save.isPending ? 'Saving' : 'Save'}
                </button>
              </form>
            )}
            {error && <p role="alert" className="mt-2 text-[13px] text-danger">{error}</p>}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Add items by search. FR-1323.
 *
 * The search is the one the purchase order picker uses (FR-1325): every word
 * must match our code, the description, the colour, the size or the
 * supplier's code, and the supplier's items come first.
 */
function AddItems({ supplierId }: { supplierId: string }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SupplierItemHit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<Record<string, string>>({});

  const search = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `/api/suppliers/${supplierId}/item-search?q=${encodeURIComponent(q.trim())}`,
        { credentials: 'same-origin' },
      );
      const json = (await res.json().catch(() => null)) as
        | { rows?: SupplierItemHit[]; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(json?.error?.message ?? `The search did not work (${res.status})`);
      return json?.rows ?? [];
    },
    onSuccess: (rows) => { setError(null); setHits(rows); },
    onError: (e: Error) => setError(e.message),
  });

  const link = useMutation({
    mutationFn: async (itemId: string) => call(`/api/suppliers/${supplierId}/items`, {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, supplier_code: code[itemId] ?? '' }),
    }),
    onSuccess: (_r, itemId) => {
      setError(null);
      setHits((h) => h?.map((x) => (x.item_id === itemId ? { ...x, linked: true } : x)) ?? null);
      void qc.invalidateQueries({ queryKey: ['admin', 'suppliers', supplierId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className={`${box} mb-3 p-3`}>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); if (q.trim()) search.mutate(); }}
      >
        <Field label="Add items" htmlFor="sup-item-q" hint="Search by code, product, colour or size">
          <input
            id="sup-item-q" className={`${inputClass} w-[320px] max-w-full`} value={q}
            placeholder="For example AS 5001 black M" onChange={(e) => setQ(e.target.value)}
          />
        </Field>
        <button type="submit" className={buttonClass('secondary', 'sm')} disabled={!q.trim() || search.isPending}>
          {search.isPending ? 'Searching' : 'Search'}
        </button>
      </form>
      {error && <p role="alert" className="mt-2 text-[13px] text-danger">{error}</p>}
      {hits && hits.length === 0 && (
        <p className="mt-2 text-[13px] text-muted">No item matches every word.</p>
      )}
      {hits && hits.length > 0 && (
        <ul className="mt-3 divide-y divide-line">
          {hits.map((h) => (
            <li key={h.item_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[13.5px]">
              <span>
                <span className="font-mono">{h.code}</span>{' '}
                <span className="text-muted">{[h.name, h.colour, h.size].filter(Boolean).join(' · ')}</span>
              </span>
              {h.linked ? (
                <span className="text-[12.5px] text-muted">On the list</span>
              ) : (
                <span className="flex items-center gap-2">
                  <label htmlFor={`add-sc-${h.item_id}`} className="sr-only">Supplier&apos;s code for {h.code}</label>
                  <input
                    id={`add-sc-${h.item_id}`} className={`${inputClass} h-9 w-[160px]`} maxLength={60}
                    placeholder="Supplier's code" value={code[h.item_id] ?? ''}
                    onChange={(e) => setCode({ ...code, [h.item_id]: e.target.value })}
                  />
                  <button
                    type="button" className={buttonClass('secondary', 'sm')} disabled={link.isPending}
                    onClick={() => link.mutate(h.item_id)}
                  >
                    Add
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
